import { IconButton, Stack, Switch, SwitchProps, Tooltip } from '@mui/material'
import { Device, Project, RuuviTag, User, UserAlertRule, UserAlertRuleInput } from '../types'
import { usePolicy } from '../hooks'
import { InfoRounded, Warning } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { ChangeEvent, useMemo, useState } from 'react'
import { RootState } from '../redux'
import { useDispatch, useSelector } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import useUserAlertRules from '../hooks/useUserAlertRules'

export interface UserAlertRuleEmailNotificationsSwitchProps extends Omit<
  SwitchProps,
  'checked' | 'onChange'
> {
  user: User
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  userAlertRule: UserAlertRule
  userAlertRules?: UserAlertRule[]
  useRedux?: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
  beforeSubmit?: (input: UserAlertRuleInput) => void
  afterSubmit?: (userAlertRules: UserAlertRule[]) => void
}

export default function UserAlertRuleEmailNotificationsSwitch({
  user,
  project,
  device,
  ruuviTag,
  userAlertRule,
  userAlertRules: initialUserAlertRules = [],
  useRedux = true,
  disabled,
  size = 'small',
  onChange,
  beforeSubmit,
  afterSubmit,
  ...props
}: UserAlertRuleEmailNotificationsSwitchProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy state.
   */
  const { canEditUserAlertRules } = usePolicy()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * The user alert rules.
   */
  const userAlertRulesFromRedux = useSelector((state: RootState) => state.users.userAlertRules)

  /**
   * The used user alert rules.
   */
  const userAlertRules = useRedux ? userAlertRulesFromRedux : initialUserAlertRules

  const { updateUserAlertRules } = useUserAlertRules({
    user,
    userAlertRules,
    project,
    device,
    ruuviTag,
    useRedux
  })

  /**
   * Check if the email notifications are enabled.
   */
  const emailNotificationsAreEnabled = user.notificationsByEmail

  /**
   * Indicates if the email notification can be disabled.
   */
  const canDisableEmailNotifications = useMemo(() => {
    if (device) {
      if (device?.asset?.projectId !== undefined && device.asset.projectId !== null) {
        const projectRule = userAlertRules.find(
          (rule) =>
            rule.alertRuleName === userAlertRule.alertRuleName &&
            rule.projectId === device.asset?.projectId
        )

        if (projectRule && projectRule.emailNotificationEnabled) {
          return false
        }
      }

      return true
    }

    return true
  }, [device, userAlertRules])

  /**
   * Handle the "change" event.
   */
  async function handleChange(event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    try {
      setLoading(true)

      const input: UserAlertRuleInput = {
        ...userAlertRule,
        emailNotificationEnabled: checked
      }

      beforeSubmit?.(input)

      const updatedUserAlertRules = await updateUserAlertRules(input)

      afterSubmit?.(updatedUserAlertRules)

      dispatch(
        createSuccessNotification(
          checked ? t('enableEmailNotificationSuccess') : t('disableEmailNotificationSuccess')
        )
      )

      onChange?.(event, checked)
    } catch (_error) {
      dispatch(
        createErrorNotification(
          checked ? t('enableEmailNotificationError') : t('disableEmailNotificationError')
        )
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {canEditUserAlertRules(user) && (
        <>
          {emailNotificationsAreEnabled ? (
            <>
              {canDisableEmailNotifications ? (
                <Switch
                  value={userAlertRule.emailNotificationEnabled}
                  checked={userAlertRule.emailNotificationEnabled}
                  disabled={disabled || loading}
                  size={size}
                  {...props}
                  onChange={handleChange}
                />
              ) : (
                <Stack direction="row" alignItems="center" gap={1} sx={{ position: 'relative' }}>
                  <Switch value={true} checked={true} disabled={true} size={size} {...props} />
                  <Tooltip title={t('alert:notificationSettingCannotBeDisabled')}>
                    <IconButton
                      size="small"
                      color="warning"
                      sx={{ position: 'absolute', right: -28 }}
                    >
                      <InfoRounded fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </>
          ) : (
            <Tooltip title={t('emailNotificationsDisabled')}>
              <IconButton size={size} color="warning">
                <Warning />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </>
  )
}
